import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiGet, registerUnauthorizedHandler } from "@/lib/api-client";
import type { AuthCheckResponse } from "@/types";

export const AUTH_QUERY_KEY = ["auth", "check"] as const;
export const AUTH_CONFIG_QUERY_KEY = ["auth", "config"] as const;

export interface AuthConfigResponse {
  turnstile_required?: boolean;
  turnstile_site_key?: string | null;
}

export function useAuthCheck() {
  const queryClient = useQueryClient();

  // Session-expiry handling: a same-origin 401 means the cookie session is
  // dead. Flip the cached auth state immediately (so route guards bounce to
  // /login without waiting on a network round-trip), then refetch
  // /api/auth/check for confirmation.
  useEffect(() => {
    return registerUnauthorizedHandler(() => {
      queryClient.setQueryData<AuthCheckResponse>(AUTH_QUERY_KEY, { authenticated: false });
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    });
  }, [queryClient]);

  return useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: () => apiGet<AuthCheckResponse>("/api/auth/check"),
    retry: false,
    staleTime: 60_000,
  });
}

export function useAuthConfig() {
  return useQuery({
    queryKey: AUTH_CONFIG_QUERY_KEY,
    queryFn: () => apiGet<AuthConfigResponse>("/api/auth/config"),
    retry: false,
    staleTime: 5 * 60_000,
  });
}
